/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { forwardRef, useMemo } from 'react';
import { media } from 'src/styles/media';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Button from 'src/components/core/buttons/button';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = TourData['sections']['nextTour'];

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: var(--color-dark);
  color: var(--color-white);
  padding: 0 var(--gutter-mobile);
  position: relative;

  ${media.min.md`
    height: 100vh;
    min-width: 150vw;
    padding: 0 var(--gutter);
  `}
`;

/**
 * `InnerWrapper` styled component.
 */

const InnerWrapper = styled.div`
  ${media.min.md`
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    width: 100vw;
  `}

  ${media.max.md`
    padding-top: 4.875rem;
    text-align: center;
  `}
`;

/**
 * `TitleText` styled component.
 */

const TitleText = styled(Text)`
  color: var(--color-yellow);
  font-size: clamp(
    ${74 / baseFontSize}rem,
    ${clampCalc(74, 132)},
    ${132 / baseFontSize}rem
  );
  letter-spacing: clamp(
    ${3.7 / baseFontSize}rem,
    ${clampCalc(3.7, 6.6)},
    ${6.6 / baseFontSize}rem
  );
  line-height: 9.9375rem;
  margin-bottom: 8rem;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 1;

  ${media.min.md`
    margin-bottom: 1rem;
  `}

  @media screen and (max-width: 479px) {
    font-size: 3.1rem;
  }
`;

/**
 * `Subtitle` styled component.
 */

const SubTitle = styled(Text)`
  color: var(--color-white);
  display: block;
  margin-top: -1.5rem;
  text-align: center;
  text-transform: none;

  ${media.min.md`
    margin-top: -2.5rem;
  `}

  ${media.max.md`
    font-size: 4.5rem;
  `}

  @supports (-webkit-hyphens: none) {
    font-size: clamp(35px, ${clampCalc(35, 80)}, 80px);
  }
`;

/**
 * `LeadStyle` styled component.
 */

const LeadStyle = styled.p`
  color: var(--color-white);
  display: block;
  font-family: var(--font-futura-pt);
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.31rem;
  margin-bottom: 0;
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `CityTitle` styled component.
 */

const CityTitle = styled(Text)`
  -webkit-text-stroke: 1px var(--color-white);
  color: var(--color-dark);
  margin-bottom: 5.875rem;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: 4rem;
    padding: 0 2rem;
  `}

  ${media.max.sm`
    font-size: 2rem;
    line-height: 2.5rem;
  `}
`;

/**
 * `ButtonStyle` styled component.
 */

const ButtonStyle = styled(Button)`
  ${media.max.md`
    margin-bottom: 5rem;
  `}

  @media screen and (max-width: 374px) {
    padding: 1.6875rem 1.25rem;
  }
`;

/**
 * `NextTourSection` component.
 */

const NextTourSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { slug, title } = props;
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');
  const routeResolver = useLocalizedRoute();
  const nextChapterUrl = useMemo(
    () => routeResolver('tourDetails', { slug }),
    [routeResolver, slug]
  );

  return (
    <Wrapper ref={ref}>
      <InnerWrapper>
        {!isMobile && (
          <FadeInRightAnimation
            options={{
              transitionDelay: 0.5,
              transitionTime: 1
            }}
          >
            <TitleText
              as={'h2'}
              variant={'boldL'}
            >
              {translate('common:sections.tour.next')}

              <SubTitle
                as={'span'}
                variant={'handwrittenM'}
              >
                {translate('common:sections.tour.chapter')}
              </SubTitle>
            </TitleText>
          </FadeInRightAnimation>
        )}

        {isMobile && (
          <FadeInUpAnimation
            options={{
              transitionDelay: 0.5,
              transitionTime: 1
            }}
          >
            <TitleText
              as={'h2'}
              variant={'boldL'}
            >
              {translate('common:sections.tour.next')}

              <SubTitle
                as={'span'}
                variant={'handwrittenM'}
              >
                {translate('common:sections.tour.chapter')}
              </SubTitle>
            </TitleText>
          </FadeInUpAnimation>
        )}

        {!isMobile && (
          <FadeInRightAnimation
            options={{
              transitionDelay: 0.6,
              transitionTime: 1
            }}
          >
            <CityTitle
              as={'p'}
              variant={'titleS'}
            >
              {title.replaceAll('-', '-⁠')}
            </CityTitle>
          </FadeInRightAnimation>
        )}

        {isMobile && (
          <FadeInUpAnimation
            options={{
              transitionDelay: 0.6,
              transitionTime: 1
            }}
          >
            <CityTitle
              as={'p'}
              variant={'titleS'}
            >
              {title}
            </CityTitle>
          </FadeInUpAnimation>
        )}

        {isMobile ? (
          <FadeInUpAnimation
            options={{
              transitionDelay: 0.7,
              transitionTime: 1
            }}
          >
            <ButtonStyle
              colorTheme={'primary'}
              href={nextChapterUrl}
              variant={'outlined'}
            >
              {translate('common:sections.tour.nextChapter')}
            </ButtonStyle>
          </FadeInUpAnimation>
        ) : (
          <FadeInRightAnimation
            options={{
              transitionDelay: 0.7,
              transitionTime: 1
            }}
          >
            <LeadStyle>
              {translate('common:sections.tour.keepScrolling')}
            </LeadStyle>
          </FadeInRightAnimation>
        )}
      </InnerWrapper>
    </Wrapper>
  );
});

NextTourSection.displayName = 'NextTourSection';

/**
 * Export `NextTourSection` component.
 */

export default NextTourSection;
